import type { PlayableTrack } from '../../stores/player';

/**
 * Cover art, or the placeholder that stands in for it.
 *
 * ## Why the placeholder is not one flat grey
 *
 * Most tracks on a young site have no artwork, so the fallback is what a visitor
 * actually sees - and a grid of identical grey squares is indistinguishable from
 * a page that failed to load. Hashing the title into one of a few brand hues
 * gives each card its own identity and, incidentally, makes a track findable by
 * where it sits and what colour it is while somebody is scrolling back to it.
 *
 * The hues are the design system's own three: indigo, red, green. Not the full
 * wheel, which lands on colours that fight the palette.
 *
 * Sizing comes entirely from `className` so this can be a 44px thumb or a 176px
 * hero tile without a `size` prop that has to enumerate every case.
 */
export function Artwork({
  track,
  className = '',
  large = false,
}: {
  track: Pick<PlayableTrack, 'title' | 'artwork' | 'artwork_small'>;
  className?: string;
  /** Ask for the 800px variant. Small tiles should not: it is eight times the bytes. */
  large?: boolean;
}) {
  const src = large ? (track.artwork ?? track.artwork_small) : (track.artwork_small ?? track.artwork);

  if (src) {
    return (
      <img
        src={src}
        alt=""
        loading="lazy"
        className={`bg-raised object-cover ${className}`}
      />
    );
  }

  const stripes = ['#2a2a5e', '#4a1e2a', '#1a3d2e'];
  let hash = 0;
  for (let i = 0; i < track.title.length; i++) hash = (hash * 31 + track.title.charCodeAt(i)) | 0;
  const base = stripes[Math.abs(hash) % stripes.length];

  return (
    <div
      aria-hidden
      className={`bg-raised ${className}`}
      style={{
        backgroundImage: `repeating-linear-gradient(45deg, ${base} 0 8px, color-mix(in oklab, ${base} 55%, var(--color-void)) 8px 16px)`,
      }}
    />
  );
}
